import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import ctaBg from '../landing/assets/about_us_4.webp';
import iconApple from '../landing/assets/Apple.png';
import iconGoogle from '../landing/assets/Playstore.png';
import liveScoringFeaturesBg from '../landing/assets/live_scoring_features_bg.webp';
import liveScoringHeroBg from '../landing/assets/live_scoring_hero_bg.webp';
import liveScoringWhyBg from '../landing/assets/live_scoring_why_bg.webp';
import liveScoringIcon1 from '../landing/assets/svgs/live_scoring_icon_2.svg';
import liveScoringIcon2 from '../landing/assets/svgs/live_scoring_icon_3.svg';
import liveScoringIcon4 from '../landing/assets/svgs/player_team_icon.svg';
import liveScoringIcon3 from '../landing/assets/svgs/scoring_icon.svg';
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

const FAQs = [
    {
        q: "What is a live scoring app, and how does it work?",
        a: <>A live scoring app allows organizers to record and update scores instantly during matches. ScorePal’s live scoring app works across devices, syncing data in real time so players, playing teams, and fans can follow live match tracking, scoreboard updates, and match statistics as the game unfolds.</>,
    },
    {
        q: "Can ScorePal be used for different sports and leagues?",
        a: <>Yes. ScorePal supports multiple sports, including Tennis, <a href="https://scorepal.co/pickleball-tournament-software/" className={"not-hover:underline"}>pickleball</a>, badminton, Padel, and more. Whether you’re managing a single match, a full league, or a tournament, the app adapts to your sport with flexible settings, reliable live scoring, and integrated sports league software features.</>,
    },
    {
        q: "Does the live scoring app support scoreboard live scoring?",
        a: <>Absolutely. ScorePal offers customizable digital scoreboards for live scoring. Display real-time scores, team names, timers, player details, and stats on screens at the venue or online, enhancing the experience for participants, spectators, and officials.</>,
    },
    {
        q: "What makes ScorePal different from other scoring apps?",
        a: <>ScorePal is designed by players, for players. Its clean interface, real-time updates, and focus on racquet sports make scorekeeping fast, accurate, and enjoyable—without unnecessary complexity.</>,
    },
    {
        q: "Can fans and players access live scores from any device?",
        a: <>ScorePal is optimized for mobile, tablet, and desktop devices. Players, teams, and fans can access live scores, fixtures, past results, and statistics anytime, anywhere. Live updates ensure everyone stays connected to the action in real time.</>,
    },
    {
        q: "How does live scoring improve tournaments and leagues?",
        a: <>Live scoring reduces the chance of errors, saves time, and keeps events running smoothly. With automatic updates to standings, brackets, and statistics, ScorePal enhances tournament league software workflows while delivering fast, accurate scoring that keeps audiences engaged and excited throughout the game.</>,
    },
];

export const Route = createFileRoute('/live-scoring')({
    component: RouteComponent,
})

function RouteComponent() {
    const features = [
        {
            icon:        liveScoringIcon1,
            title:       'Real-Time Match Scoring',
            alt:         'live scoring icon',
            description: 'Record points, fouls, timeouts, and more with a clean, easy-to-use interface. Updates are synced instantly across all connected devices, ensuring everyone stays on the same page.',
        },
        {
            icon:        liveScoringIcon2,
            title:       'Instant Score Sharing',
            alt:         'live scoring icon',
            description: 'Share live scores to your website, social feeds, display boards, or the ScorePal platform. Keep spectators informed—even if they can\'t be at the venue.',
        },
        {
            icon:        liveScoringIcon3,
            title:       'Dynamic Scoreboards',
            alt:         'scoring icon',
            description: 'Elevate your event with customizable digital scoreboards. Display team names, logos, timers, and real-time stats to create a professional game-day experience.',
        },
        {
            icon:        liveScoringIcon4,
            title:       'Powerful Event Management Tools',
            alt:         'player team',
            description: 'ScorePal integrates scoring with your broader event operations—automatically updating standings, results, and tournament brackets as you score.',
        },
    ];

    const whyChooseItems = [
        'Fast, accurate match scoring',
        'Ideal for tournaments, leagues, and single events',
        'Real-time syncing across devices',
        'Professional digital scoreboard options',
        'Easy setup—no technical expertise required',
    ];

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context":   "https://schema.org",
                "@type":      "FAQPage",
                "mainEntity": [
                    {
                        "@type":          "Question",
                        "name":           "What is a live scoring app, and how does it work?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "A live scoring app allows organizers to record and update scores instantly during matches. ScorePal’s live scoring app works across devices, syncing data in real time so players, playing teams, and fans can follow live match tracking, scoreboard updates, and match statistics as the game unfolds."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can ScorePal be used for different sports and leagues?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes. ScorePal supports multiple sports, including Tennis, pickleball, badminton, racquetball, and more. Whether you’re managing a single match, a full league, or a tournament, the app adapts to your sport with flexible settings, reliable live scoring, and integrated sports league software features."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Does the live scoring app support scoreboard live scoring?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Absolutely. ScorePal offers customizable digital scoreboards for live scoring. Display real-time scores, team names, timers, player details, and stats on screens at the venue or online, enhancing the experience for participants, spectators, and officials."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "What makes ScorePal different from other scoring apps?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "ScorePal is designed by players, for players. Its clean interface, real-time updates, and focus on racquet sports make scorekeeping fast, accurate, and enjoyable—without unnecessary complexity."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can fans and players access live scores from any device?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "ScorePal is optimized for mobile, tablet, and desktop devices. Players, teams, and fans can access live scores, fixtures, past results, and statistics anytime, anywhere. Live updates ensure everyone stays connected to the action in real time."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "How does live scoring improve tournaments and leagues?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Live scoring reduces the chance of errors, saves time, and keeps events running smoothly. With automatic updates to standings, brackets, and statistics, ScorePal enhances tournament league software workflows while delivering fast, accurate scoring that keeps audiences engaged and excited throughout the game."
                        }
                    }
                ]
            }} />
            <JsonLd id={`page${Route.id}/app`} item={{
                "@context":            "https://schema.org",
                "@type":               "SoftwareApplication",
                "name":                "Live Scoring Software",
                "url":                 "https://scorepal.co/live-scoring/",
                "applicationCategory": "SportsApplication",
                "operatingSystem":     ["Web", "Android", "iOS"],
                "description":         "Real-time live scoring system for tournaments and matches.",
                "offers":              {
                    "@type":         "Offer",
                    "price":         "0",
                    "priceCurrency": "USD"
                }
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Live Scoring", item: "https://scorepal.co/live-scoring/"}]} />
            <title>Live Scoring Software for Sports | Real-Time Match Updates | ScorePal</title>
            <meta name="description" content="Live scoring software for sports tournaments and leagues with real-time updates, automated results, and seamless match tracking for clubs." />
            <link rel="canonical" href={`${config.publicUrl}/live-scoring/`} />

            <section className="relative overflow-hidden p-4">
                <div
                    className="flex flex-col min-h-screen justify-between relative pb-16 rounded-4xl bg-[#EDEEF4] bg-top bg-cover bg-no-repeat saturate-[0.87]"
                    style={{
                        backgroundImage: `url(${liveScoringHeroBg})`
                    }}
                >
                    <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/30 to-transparent rounded-4xl" />

                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8 flex-1 flex flex-col justify-center relative z-10">
                        <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-4">
                            Live Scoring Software for Sports
                        </h1>

                        <p className="font-inter text-white/90 text-base lg:text-xl mb-8 max-w-2xl leading-relaxed">
                            Keep every match updated in real time with ScorePal’s live scoring system for tournaments, leagues, and club competitions.
                        </p>

                        <Link
                            to={"/signup/player/" as any}
                            className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>

                        <div className="flex flex-wrap gap-4 mt-16 lg:mt-60">
                            <a
                                href="https://play.google.com/store/apps/details?id=co.scorepal.mobile&pcampaignid=web_share"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-100 transition">
                                    <img src={iconGoogle} alt="Google Play" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">GET IT ON</div>
                                        <div className="text-white font-semibold text-sm">Google Play</div>
                                    </div>
                                </div>
                            </a>

                            <a
                                href="https://apps.apple.com/us/app/score-pal/id6745509167"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                                    <img src={iconApple} alt="App Store" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">Download on the</div>
                                        <div className="text-white font-semibold text-sm">App Store</div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="rounded-xl m-4 bg-[#E3E6EE]">
                <div className="py-16 lg:py-24">
                    <div className="container mx-auto px-6 lg:px-12">
                        <div className="text-center mb-12">
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                REAL-TIME MATCH SCORING MADE <span className="text-[#082F8A]">SIMPLE</span>
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed max-w-4xl mx-auto">
                                Bring every game to life with ScorePal's live scoring app—the fastest, most intuitive way to score matches in real time. Whether you're managing tournaments, leagues, school sports, or community events, <a href="https://scorepal.co/" className={"not-hover:underline"}>ScorePal</a> covers a wide range of sports and scoring needs, delivering professional-grade tools for accurate, instant updates to players, fans, and officials.
                            </p>
                        </div>

                        <div className="block lg:hidden mb-4">
                            <img
                                src={liveScoringFeaturesBg}
                                alt="Live scoring app screenshots"
                                className="w-full h-auto object-contain"
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                            <div className="flex flex-col gap-6">
                                {features.map((feature, index) => (
                                    <div key={index} className="">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0">
                                                <img src={feature.icon} alt="" className="w-5 h-5" />
                                            </div>
                                            <h3 className="font-inter text-[#1F2022] font-semibold text-base">
                                                {feature.title}
                                            </h3>
                                        </div>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">
                                            {feature.description}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            <div className="hidden lg:block">
                                <img
                                    src={liveScoringFeaturesBg}
                                    alt="Live scoring app screenshots"
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-center">
                    <Link
                        to={"/signup/player/" as any}
                        className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                    >
                        <span className="font-inter text-base font-medium">Lets Get Started</span>
                        <div className="bg-[#082F8A] rounded-full p-1">
                            <ArrowUpRightIcon className="text-white" size={28} />
                        </div>
                    </Link>
                </div>

            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div className="hidden lg:block">
                            <img
                                src={liveScoringWhyBg}
                                alt="Why ScorePal Live Scoring"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>

                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                                WHY SCOREPAL LIVE SCORING?
                            </h2>

                            <ul className="space-y-4">
                                {whyChooseItems.map((item, index) => (
                                    <li key={index} className="flex items-center gap-4">
                                        <CheckBadgeIcon size={24} />
                                        <span className="font-inter text-[#1a1a2e] text-base">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="block lg:hidden">
                            <img
                                src={liveScoringWhyBg}
                                alt="Why the ScorePal Live Scoring App?"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-8 lg:py-16">
                <div className="container mx-auto px-6 lg:px-12">
                    <div
                        className="rounded-3xl py-16 lg:py-24 px-8 text-center bg-top bg-cover bg-no-repeat"
                        style={{backgroundImage: `url(${ctaBg})`}}
                    >
                        <h2 className="font-bebas text-white uppercase text-[clamp(28px,4vw,48px)] leading-tight font-bold tracking-wide mb-4">
                            ENHANCE EVERY GAME WITH LIVE SCORING
                        </h2>
                        <p className="font-inter text-white/90 text-base lg:text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
                            From grassroots competitions to large-scale events, ScorePal's live scoring tools help you run smoother games and engage your audience like never before. Deliver instant updates, reduce scoring errors, and create a modern, dynamic experience for everyone involved.
                            <br /><br />
                            Upgrade your events with ScorePal's live scoring app today.
                        </p>
                        <Link
                            to={"/signup/player/" as any}
                            className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                    <FaqComponent title={"Faqs About Live Scoring App"} faqs={FAQs} />
                </div>
            </section>

            <FooterSection />
        </div>
    );
}
